import React from 'react';
import Grid from '@mui/material/Grid';

import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { APICONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';

import { SnackContext, AppContext, ThemeContext } from 'src/store/ContextStore';
import { getAddressFlags, getDependencies } from './formService';
import { useAddressGeo } from 'src/util/geo/useAddressGeo';
import { useAddressAutocomplete } from 'src/util/geo/useAddressAutocomplete';

import TextInput from 'src/components/UI/input/TextInput';
import SelectInput from 'src/components/UI/input/SelectInput';
import AddressAutocompleteInput from 'src/components/UI/input/AddressAutocompleteInput';
import AcitonDialog from 'src/components/UI/dialog/AcitonDialog';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import { useAutoTranslation } from 'src/hooks/useTranslate';

export default function AddressForm({
  handleClose,
  familyMemberId,
  familyId,
  isEditing,
  model,
  setUnsavedChanges
}) {
  const t = useAutoTranslation();

  const { content } = React.useContext(AppContext);
  const { theme } = React.useContext(ThemeContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { setSnack } = React.useContext(SnackContext);

  const defaultValues = isEditing
    ? { ...model }
    : {
        familyId: user.familyId,
        familyMemberId: familyMemberId,
        addressLine1: '',
        addressLine2: '',
        town: '',
        city: '',
        county: '',
        state: '',
        postCode: '',
        country: '',
        correspondenceAddress: true,
        primaryAddress: true
      };
  const [formValues, setFormValues] = React.useState(defaultValues);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const [addressFlag, setAddressFlag] = React.useState({
    hasPrimaryAddress: false,
    hasCorrespondenceAddress: false,
    primaryAddressId: null,
    correspondenceAddressId: null
  });

  React.useEffect(() => {
    getAddressFlags({
      familyId: familyId,
      familyMemberId: familyMemberId
    })
      .then((flag) => setAddressFlag(flag))
      .catch((err) =>
        setSnack({
          status: true,
          msg: `${t('An error occured')} `,
          severity: 'error'
        })
      );
  }, []);

  const [error, setError] = React.useState({});

  const seededCountryRef = React.useRef(isEditing);
  const [countryTouched, setCountryTouched] = React.useState(false);

  const handleChange = (e) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.value
    }));
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const applyPatch = (patch) => {
    setFormValues((prevValues) => ({ ...prevValues, ...patch }));
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const geo = useAddressGeo({
    country: formValues.country,
    subdivision: formValues.state,
    postCode: formValues.postCode,
    onChange: applyPatch
  });

  const lookup = useAddressAutocomplete({
    countryCodes:
      countryTouched && geo.countryCode ? [geo.countryCode] : undefined,
    reference: geo.reference,
    values: formValues,
    onChange: applyPatch
  });
  const defaultCountry =
    lookup.siteCountries.length === 1 ? lookup.siteCountries[0] : '';

  React.useEffect(() => {
    if (seededCountryRef.current || !defaultCountry) return;
    seededCountryRef.current = true;
    setFormValues((prev) =>
      prev.country ? prev : { ...prev, country: defaultCountry }
    );
  }, [defaultCountry]);

  //Sure about this! Will be good for show in Family
  const onToggleSwitch = (e) => {
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.checked
    }));
    if (familyMemberId == 'none') {
      setFormValues((prevValues) => ({
        ...prevValues,
        showInFamily: true
      }));
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
  };
  const handleOpenModal = () => {
    setOpen(true);
  };

  const deleteNote = async (e) => {
    setLoading(true);

    const dep = await getDependencies(
      user.familyId,
      model._id,
      theme.dateFormat,
      t
    );
    if (dep.length !== 0) {
      setSnack({
        status: true,
        msg: t('Associated record exists; check the {{value}}', {
          value: dep[0]
        }),
        severity: 'error'
      });
      setLoading(false);
      return;
    }
    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then((res) => {
        setSnack({
          status: true,
          msg: t('Record deleted'),
          severity: 'success'
        });
        handleCloseModal();
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Delete failed! {{err}}', { err }),
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const [confirmationDialog, setConfirmationDialog] = React.useState({});

  const onCancel = () => {
    setConfirmationDialog({ open: false });
  };

  const saveAddressAndClose = () => {
    return axios
      .put(APICONSTANTS.address, formValues)
      .then((res) => {
        handleClose({ exit: true });
        if (res.status === 200) {
          setSnack({
            status: true,
            msg: t('Successfully saved'),
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: t('Upload failed!'),
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        const fieldErrors = err?.response?.data?.fieldErrors;
        if (fieldErrors) setError(fieldErrors);
        setSnack({
          status: true,
          msg:
            err?.response?.data?.error || t('Upload failed! {{err}}', { err }),
          severity: 'error'
        });
      });
  };

  const validateForm = (values) => {
    const errors = {};
    const hardErrors = { ...geo.validate() };
    if (!values.country) {
      hardErrors.country = 'Country is required';
    }
    if (values.country && geo.hasSubdivisionList && !values.state) {
      hardErrors.state = `${geo.subdivisionLabel} is required`;
    }
    if (!values.addressLine1 && !values.town && !values.city) {
      const message =
        'At least one of Address Line 1, Town, or City must be provided';
      hardErrors.addressLine1 = message;
      hardErrors.town = message;
      hardErrors.city = message;
    }

    if (Object.keys(hardErrors).length > 0) {
      setError(hardErrors);
      return hardErrors;
    }
    if (
      !(addressFlag.primaryAddressId === formValues._id) &&
      formValues.primaryAddress &&
      addressFlag.hasPrimaryAddress
    ) {
      errors.primaryAddress = t('Primary Address exists');
      setConfirmationDialog({
        open: true,
        title: 'Change Primary Address',
        message:
          'Please note that saving this address as Primary Address will overwrite the primary flag on other address record. Please confirm you wish to proceed',
        onConfirm: () => {
          saveAddressAndClose();
        },
        onCancel: onCancel
      });
    } else if (
      !(addressFlag.correspondenceAddressId === formValues._id) &&
      formValues.correspondenceAddress &&
      addressFlag.hasCorrespondenceAddress
    ) {
      errors.correspondenceAddress = t('Correspondence Address exists');
      setConfirmationDialog({
        open: true,
        title: 'Change Correspondence Address',
        message:
          'Please note that saving this address as Correspondence Address will overwrite the primary flag on other address record. Please confirm you wish to proceed',
        onConfirm: () => {
          saveAddressAndClose();
        },
        onCancel: onCancel
      });
    }

    setError(errors);
    return errors;
  };

  //Think about this
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const errors = validateForm(formValues);

    if (Object.keys(errors).length > 0) return Promise.reject();

    return saveAddressAndClose();
  };

  return (
    //Should be fine to reuse old class?

    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Address ?"
        content={t(
          'Warning: This action will permanently delete this Address. Once deleted, this information will be permanently removed and cannot be restored.'
        )}
      />
      <Grid container spacing={2}>
        {lookup.show && (
          <Grid size={12}>
            <AddressAutocompleteInput
              inputValue={lookup.inputValue}
              suggestions={lookup.suggestions}
              loading={lookup.loading}
              statusMessage={lookup.statusMessage}
              onInputChange={lookup.handleInputChange}
              onSelect={lookup.handleSelect}
            />
          </Grid>
        )}

        <Grid size={12}>
          <TextInput
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="addressLine1"
            value={formValues.addressLine1}
            label="Address Line 1"
            error={!!error.addressLine1}
            helperText={error.addressLine1}
          />
        </Grid>

        <Grid size={12}>
          <TextInput
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="addressLine2"
            value={formValues.addressLine2}
            label="Address Line 2"
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="town"
            value={formValues.town}
            label="Town"
            error={!!error.town}
            helperText={error.town}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="city"
            value={formValues.city}
            label="City"
            error={!!error.city}
            helperText={error.city}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="county"
            value={formValues.county}
            label="County"
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBlock: 0.25 }}>
            <FormLabel component="label" className="form-label" required>
              Country
            </FormLabel>
          </Grid>
          <SelectInput
            variant="outlined"
            fullWidth
            onChange={(e) => {
              setCountryTouched(true);
              geo.handleCountryChange(e.target.value);
            }}
            name="country"
            value={geo.countryCode || ''}
            options={geo.countryOptions}
            disabled={geo.isLoading || geo.isError}
            required
            error={!!error.country || geo.hasUnrecognisedCountry || geo.isError}
            helperText={
              geo.isError
                ? 'Country list unavailable — try reloading'
                : geo.hasUnrecognisedCountry
                ? `"${formValues.country}" is not a standard country name — please re-select`
                : error.country || ''
            }
          />
        </Grid>

        <Grid size={6}>
          {geo.hasSubdivisionList ? (
            <>
              <Grid sx={{ paddingBlock: 0.25 }}>
                <FormLabel component="label" className="form-label" required>
                  {geo.subdivisionLabel}
                </FormLabel>
              </Grid>
              <SelectInput
                variant="outlined"
                fullWidth
                onChange={(e) => geo.handleSubdivisionChange(e.target.value)}
                name="state"
                value={
                  geo.hasUnrecognisedSubdivision ? '' : formValues.state || ''
                }
                options={geo.subdivisionOptions}
                required
                error={!!error.state || geo.hasUnrecognisedSubdivision}
                helperText={
                  geo.hasUnrecognisedSubdivision
                    ? `Currently "${
                        formValues.state
                      }" — not a standard ${geo.subdivisionLabel.toLowerCase()}, please re-select`
                    : error.state || ''
                }
              />
            </>
          ) : (
            <TextInput
              variant="outlined"
              fullWidth
              onChange={handleChange}
              name="state"
              value={formValues.state}
              label={geo.subdivisionLabel}
            />
          )}
        </Grid>

        <Grid size={6}>
          <TextInput
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="postCode"
            value={formValues.postCode}
            label="Postcode"
            disabled={!geo.hasPostcode}
            error={!!error.postCode || geo.postCodeError}
            helperText={
              error.postCode ||
              geo.postCodeHelperText ||
              (!geo.hasPostcode && formValues.country
                ? t('{{value}} does not use postcodes', {
                    value: geo.countryMeta?.name || formValues.country
                  })
                : '')
            }
          />
        </Grid>

        <Grid
          container
          size={12}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid
            size={{
              xs: 6,
              sm: 7
            }}
          >
            <FormLabel component="label" className="form-label">
              Correspondence Address
            </FormLabel>
          </Grid>
          <Grid
            container
            size={{
              xs: 6,
              sm: 3
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Switch
                color="secondary"
                checked={formValues.correspondenceAddress ?? false}
                onChange={onToggleSwitch}
                name="correspondenceAddress"
              />
            </Grid>
            <Grid>
              <Typography>
                {formValues.correspondenceAddress ? t('Yes') : t('No')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          size={12}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid
            size={{
              xs: 6,
              sm: 7
            }}
          >
            <FormLabel component="label" className="form-label">
              Primary Address
            </FormLabel>
          </Grid>
          <Grid
            container
            size={{
              xs: 6,
              sm: 3
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Switch
                color="secondary"
                checked={formValues.primaryAddress ?? false}
                onChange={onToggleSwitch}
                name="primaryAddress"
              />
            </Grid>
            <Grid>
              <Typography>
                {formValues.primaryAddress ? t('Yes') : t('No')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <ModalButtons
          isEditing={isEditing}
          hideContinueAdding={true}
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing ? handleOpenModal : null}
          handleClose={handleClose}
          onClick={handleSubmit}
          addLabel={isEditing ? 'Add new address' : 'Save New Address'}
        />
      </Grid>
      <ConfirmationDialog confirmationDialog={confirmationDialog} />
    </form>
  );
}

const ConfirmationDialog = ({ confirmationDialog }) => {
  const { open, message, onConfirm, onCancel, title } = confirmationDialog;

  const handleClose = () => onCancel();

  return (
    <AcitonDialog
      open={open}
      handleClose={handleClose}
      onSubmit={onConfirm}
      title={title}
    >
      <Typography>{message}</Typography>
    </AcitonDialog>
  );
};
